import {
    ChangeDetectionStrategy,
    Component,
    computed,
    CUSTOM_ELEMENTS_SCHEMA,
    effect,
    ElementRef,
    EnvironmentInjector,
    inject,
    input,
    model,
    OnInit,
    runInInjectionContext,
    viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';

import { BaasAppState, BaasConnectivity, BaasSecurityLevel } from '@lvflanders/bar';
import { BarCoreService } from '@lvflanders/bar-ng';
import {
    BaasActionButtonOptions,
    BaasExternalLinkGroupOptions,
    BaasHeroOptions,
    BaasLayout,
    BaasLayoutService,
    BaasMenuButtonItemOptions,
    BaasMenuItemOptions,
    BaasOffcanvasOptions,
    BarAppMenuItem,
    BarMenuState
} from '@lvflanders/bar-ng-ui';
import { BarChatLvConnectConfig } from '@lvflanders/bar-ui';
import {
    StartschermHelpWidget,
    StartschermTestProfileWidget,
    StartschermUserWidget,
    StartschermV2StateService,
    StartschermVoorwieWidget
} from '@lvflanders/startscherm-ng-ui';

import { BaasContainerBurgerprofielService } from '../burgerprofiel/baas-container-burgerprofiel.service';
import { defineAppMenu } from '../define-app-menu.function';

@Component({
    selector: 'baas-container',
    templateUrl: './baas-container.html',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [BaasLayout, StartschermTestProfileWidget, StartschermVoorwieWidget, StartschermUserWidget, StartschermHelpWidget],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BaasContainer implements OnInit {
    readonly #injector = inject(EnvironmentInjector);
    readonly #router = inject(Router);
    readonly #burgerprofiel = inject(BaasContainerBurgerprofielService);
    readonly #layout = inject(BaasLayoutService);
    readonly #coreService = inject(BarCoreService);
    readonly #startschermStateService = inject(StartschermV2StateService);
    readonly state = inject(BaasAppState);

    readonly chat = viewChild<ElementRef<HTMLElement>>('chat');

    readonly title = input<string>(undefined);
    readonly subTitle = input<string>(undefined);
    readonly mainHeader = input<string>(undefined);
    readonly helpLink = input<string>(undefined);
    readonly withRouting = input<boolean>(undefined);
    readonly menuItems = input<BaasMenuItemOptions[]>(undefined);
    readonly menuItemsAutoCollapse = input<boolean>(undefined);
    readonly externalLinkGroups = input<BaasExternalLinkGroupOptions[]>(undefined);
    readonly sidebar = input<boolean>(undefined);
    readonly submitActions = input<BaasActionButtonOptions[]>(undefined);
    readonly moreActions = input<BaasMenuButtonItemOptions[]>(undefined);
    readonly contentActions = input<BaasActionButtonOptions[]>(undefined);
    readonly operations = input<BaasActionButtonOptions[]>(undefined);
    readonly strikingActions = input<BaasActionButtonOptions[]>(undefined);
    readonly hideActionBarContentLeft = input<boolean>(undefined);
    readonly hideBuiltInUtilities = input<boolean>(undefined);
    readonly appMenuItems = model<BarAppMenuItem[]>(undefined);
    readonly menuState = model<BarMenuState>(undefined);
    readonly offcanvasOpened = model<boolean>(false);
    readonly offcanvasPinned = model<boolean>(false);
    readonly offcanvasOptions = input<BaasOffcanvasOptions>(undefined);

    readonly compact = this.#layout.compact;
    readonly touch = this.#layout.touch.asReadonly();
    readonly mobileFriendly = this.#layout.mobileFriendly.asReadonly();

    readonly hero = input<BaasHeroOptions>(undefined);

    readonly showUtilitiesInternal = input<boolean>(true);

    readonly effectiveHelpLink = computed(() => this.helpLink() || this.#startschermStateService.helpLink());

    protected forceHideBox: boolean;
    protected connectivityOffline: boolean;
    protected hideAll = true;
    protected showTestProfielen: boolean;
    protected showVoorwie: boolean;
    protected classicWidgetsModus: boolean;
    protected chatbotConfig: { enabled: boolean; lvConnect: BarChatLvConnectConfig };

    constructor() {
        this.#router.events.pipe(takeUntilDestroyed()).subscribe((val) => {
            if (val instanceof NavigationEnd) {
                this.#defineAppMenu();
            }
        });
        // regenerate whenever basiscontext changes
        this.#coreService.basiscontextChanged.pipe(takeUntilDestroyed()).subscribe(() => this.#defineAppMenu());

        effect(() => {
            // trigger when help link is defined/changed
            this.#burgerprofiel.defineContacts(this.effectiveHelpLink());
        });
    }

    ngOnInit(): void {
        // in case we don't have the vl global variable we are running with the old widgets
        this.classicWidgetsModus = !this.#burgerprofiel.visible;
        // no layout box in case burgerprofiel widgets are used
        this.forceHideBox = this.#burgerprofiel.visible;

        this.connectivityOffline = this.state.connectivity === BaasConnectivity.Offline;
        let isPrd: boolean;
        let isIntern: boolean;
        // state.toepassing is needed for all startscherm utilities
        if (this.state.toepassing) {
            isIntern = this.state.toepassing.zone === 'I';
            isPrd = this.state.toepassing.securityLevel === BaasSecurityLevel.Production;
            this.hideAll = false;
        } else {
            this.hideAll = true;
            isIntern = true;
            isPrd = false;
        }

        this.showTestProfielen = isIntern && !isPrd;
        this.showVoorwie = !isIntern;
        const chatbotConfig = this.state.config?.baasStatic?.chatbot;
        this.chatbotConfig = {
            enabled:
                (chatbotConfig?.enabled || chatbotConfig.enabledAppCodes?.indexOf(this.state.toepassing.code) > -1) &&
                chatbotConfig.disabledAppCodes?.indexOf(this.state.toepassing.code) == -1,
            lvConnect: chatbotConfig?.lvConnect
        };
        //define the app menu at least once
        this.#defineAppMenu();
    }

    protected onCompactChange(compact: boolean) {
        this.compact.set(compact);
    }

    #defineAppMenu() {
        runInInjectionContext(this.#injector, () => {
            this.appMenuItems.set(defineAppMenu());
        });
    }
}
